import { Box, styled } from "@mui/material";
import { ReactNode } from "react";

import { palette } from "~frontend/theme";

export const EditButtons = styled("div")(() => ({
  display: "flex",
  "& svg": {
    fontSize: ".8em",
    color: palette.darkGray,
  },
}));

const ScriptKeywordsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  color: palette.navy,
  fontSize: "12px",
}));

export const KeywordHelp = styled("div")(({ theme }) => ({
  fontSize: "14px",
  borderTopColor: palette.midGray,
  borderTopWidth: 1,
  borderTopStyle: "solid",
  paddingTop: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

export const ScriptKeywordsTitle = styled("span")(() => ({
  fontWeight: 600,
}));

export const ScriptKeywordBody = styled("span")(() => ({
  fontWeight: 400,
}));

export const ScriptKeywordDetails = styled("div")(() => ({
  display: "flex",
  flexGrow: 1,
  alignItems: "center",
  paddingLeft: "16px",
  backgroundColor: palette.lightGray,
  fontSize: "14px",
}));

export const ScriptKeywords = ({
  keywords,
}: {
  keywords: { normalizedKeyword: string }[];
}): ReactNode => {
  if (!keywords.length) {
    return null;
  }
  return (
    <ScriptKeywordsContainer>
      <ScriptKeywordsTitle>Keywords:&nbsp;</ScriptKeywordsTitle>
      <ScriptKeywordBody>
        {keywords
          .map(
            ({ normalizedKeyword }) => `“${normalizedKeyword.toUpperCase()}”`
          )
          .join(", ")}
      </ScriptKeywordBody>
    </ScriptKeywordsContainer>
  );
};

export const ScriptTags = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  gap: "4px",
}));

export const ReorderAndTitleContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "start",
});

export const ScriptGroupWrapper = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

export const ReorderButtonsContainer = styled("div")(() => ({ theme }) => ({
  // Allows up/down buttons to overflow and not impact the height
  height: theme.spacing(2),

  display: "flex",
  flexDirection: "column",
  marginTop: "-12px",
  marginRight: theme.spacing(2),
  "& svg": {
    transform: "scale(0.70)",
  },
  "& button": {
    padding: 0,
    margin: 0,
    // Replace circle ripple that overlaps with other button
    color: palette.midGray,
  },
  "& button:disabled": {
    color: palette.midGray,
  },
  "& button:first-of-type": {
    borderRadius: "50% 50% 0% 0%",
  },
  "& button:last-of-type": {
    marginTop: -4,
    borderRadius: "0% 0% 50% 50%",
  },
}));

export const ScriptGroup = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#F9F8FB",
  marginBottom: theme.spacing(1),
}));

export const ScriptGroupHeading = styled(Box)({
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: "21px",
  alignItems: "center",
  justifyContent: "space-between",
  display: "flex",
  padding: "24px 18px 24px 24px",
});

export const ScriptGroupButtons = styled("div")({
  display: "flex",
  gap: 0,
  alignItems: "center",
  "& svg": {
    color: palette.darkGray,
  },
});

export const ScriptResponses = styled("div")(({ theme }) => ({
  display: "flex",
  paddingLeft: theme.spacing(5),
  flexDirection: "column",
  alignSelf: "stretch",
}));

export const ScriptResponse = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottomColor: "#F0EEF5",
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  "&:first-of-type": {
    borderTopColor: "#F0EEF5",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
  },
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "normal",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: 0,
  minWidth: "100%",
}));

export const ScriptContent = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

export const ScriptResponseTitle = styled("div")(() => ({
  fontWeight: 600,
}));

export const ScriptResponseScript = styled("div")(({ theme }) => ({
  marginLeft: theme.spacing(5),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

export const ScriptButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  "& svg": {
    fontSize: "0.9em",
    color: palette.darkGray,
    transform: "scale(0.875)",
  },
  marginRight: theme.spacing(5),
  gap: 0,
  marginTop: theme.spacing(1),
  marginLeft: theme.spacing(2),
}));

export const ScriptFormDialogButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}));

export const KeywordContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignContent: "space-between",
  gap: theme.spacing(1),
  "& button": {
    display: "flex",
    alignSelf: "flex-end",
    marginBottom: theme.spacing(0.5),
  },
}));
